import axios from 'axios';
import { compressImage } from './imageCompress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 20000,
});

// Attach bearer token as fallback if cookie unavailable (mobile / cross-site)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;

// Resolve a stored media path (e.g. "/api/uploads/<id>") to a full URL.
export const mediaUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${BACKEND_URL}${u}`;
};

// Upload an image file, returns the stored relative url (e.g. "/api/uploads/<id>").
// Large images are downscaled/compressed in-browser first so they stay within
// proxy limits and upload quickly (applies everywhere: centers, teachers, programs, etc.).
export const uploadImage = async (file) => {
  let optimized = file;
  try { optimized = await compressImage(file); } catch { optimized = file; }
  if (!(optimized instanceof Blob)) optimized = file;
  const fd = new FormData();
  // Always pass an explicit filename so the part is treated as a file (avoids 422).
  const filename = (optimized && optimized.name) || (file && file.name) || 'image.jpg';
  fd.append('file', optimized, filename);
  const res = await api.post('/uploads', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  return res.data.url;
};

// Upload a media file (image/audio/video) to the meditation media endpoint (admin).
// No in-browser compression — used for audio/video where compression doesn't apply.
export const uploadMedia = async (file) => {
  const fd = new FormData();
  fd.append('file', file, (file && file.name) || 'media');
  const res = await api.post('/admin/meditations/upload-media', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return res.data.url;
};

// Upload a media file (image/audio/video) via the contributor-accessible endpoint
// (used by the Blog composer / audio-blog for admins and content contributors).
export const uploadContentMedia = async (file) => {
  const fd = new FormData();
  fd.append('file', file, (file && file.name) || 'media');
  const res = await api.post('/assistant/upload-media', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return res.data;
};

// Turn any API error into a readable string (never returns an object/array,
// so it is always safe to render in a toast / JSX).
export const errText = (e, fallback = 'Something went wrong. Please try again.') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.message || (typeof x === 'string' ? x : '')).filter(Boolean).join(', ') || fallback;
  if (d && typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return e?.message || fallback;
};
